import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const QuickActions = ({ onQuickAction, onExportData }) => {
  const [isManualOrderOpen, setIsManualOrderOpen] = useState(false);
  const [manualOrder, setManualOrder] = useState({
    symbol: '',
    side: 'buy',
    type: 'market',
    quantity: '',
    price: '',
    venue: 'nse'
  });

  const sideOptions = [
    { value: 'buy', label: 'Buy' },
    { value: 'sell', label: 'Sell' }
  ];

  const typeOptions = [
    { value: 'market', label: 'Market' },
    { value: 'limit', label: 'Limit' },
    { value: 'stop_loss', label: 'Stop Loss' },
    { value: 'stop_limit', label: 'Stop Limit' }
  ];

  const venueOptions = [
    { value: 'nse', label: 'NSE' },
    { value: 'bse', label: 'BSE' },
    { value: 'binance', label: 'Binance' },
    { value: 'kucoin', label: 'KuCoin' }
  ];

  const handleSubmitManualOrder = () => {
    if (manualOrder?.symbol && manualOrder?.quantity) {
      onQuickAction('manual_order', manualOrder);
      setManualOrder({
        symbol: '',
        side: 'buy',
        type: 'market',
        quantity: '',
        price: '',
        venue: 'nse'
      });
      setIsManualOrderOpen(false);
    }
  };

  const quickActionButtons = [
    {
      label: 'Pause All',
      icon: 'Pause',
      action: 'pause_all',
      variant: 'outline',
      description: 'Pause all pending orders'
    },
    {
      label: 'Resume All',
      icon: 'Play',
      action: 'resume_all',
      variant: 'outline',
      description: 'Resume paused orders'
    },
    {
      label: 'Cancel Failed',
      icon: 'X',
      action: 'cancel_failed',
      variant: 'outline',
      description: 'Cancel all failed orders'
    },
    {
      label: 'Priority Mode',
      icon: 'Zap',
      action: 'priority_mode',
      variant: 'outline',
      description: 'Enable high-priority execution'
    }
  ];

  return (
    <div className="space-y-4">
      {/* Quick Action Buttons */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {quickActionButtons?.map((button, index) => (
            <Button
              key={index}
              variant={button?.variant}
              size="sm"
              onClick={() => onQuickAction(button?.action)}
              iconName={button?.icon}
              iconSize={16}
              className="flex-col h-auto py-3 text-center"
              title={button?.description}
            >
              <span className="text-xs mt-1">{button?.label}</span>
            </Button>
          ))}
        </div>
      </div>
      {/* Manual Order Entry */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Manual Order Entry</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsManualOrderOpen(!isManualOrderOpen)}
              iconName={isManualOrderOpen ? "ChevronUp" : "ChevronDown"}
              iconSize={16}
            >
              {isManualOrderOpen ? 'Collapse' : 'Expand'}
            </Button>
          </div>
        </div>

        {isManualOrderOpen && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Symbol"
                type="text"
                placeholder="e.g., RELIANCE, BTCUSDT"
                value={manualOrder?.symbol}
                onChange={(e) => setManualOrder({...manualOrder, symbol: e?.target?.value?.toUpperCase()})}
                required
              />
              <Input
                label="Quantity"
                type="number"
                placeholder="Enter quantity"
                value={manualOrder?.quantity}
                onChange={(e) => setManualOrder({...manualOrder, quantity: e?.target?.value})}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Select
                label="Side"
                options={sideOptions}
                value={manualOrder?.side}
                onChange={(value) => setManualOrder({...manualOrder, side: value})}
              />
              <Select
                label="Order Type"
                options={typeOptions}
                value={manualOrder?.type}
                onChange={(value) => setManualOrder({...manualOrder, type: value})}
              />
              <Select
                label="Venue"
                options={venueOptions}
                value={manualOrder?.venue}
                onChange={(value) => setManualOrder({...manualOrder, venue: value})}
              />
            </div>

            {(manualOrder?.type === 'limit' || manualOrder?.type === 'stop_limit') && (
              <Input
                label="Price"
                type="number"
                step="0.01"
                placeholder="Enter price"
                value={manualOrder?.price}
                onChange={(e) => setManualOrder({...manualOrder, price: e?.target?.value})}
                required
              />
            )}

            <div className="flex items-center justify-between pt-4 border-t border-border">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="AlertTriangle" size={16} />
                <span>Manual orders bypass AI validation</span>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsManualOrderOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSubmitManualOrder}
                  iconName="Send"
                  iconSize={14}
                  disabled={!manualOrder?.symbol || !manualOrder?.quantity}
                >
                  Submit Order
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Export Options */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-4">Export Data</h3>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExportData('execution_report')}
            iconName="FileText"
            iconSize={16}
          >
            Execution Report
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExportData('performance_metrics')}
            iconName="BarChart3"
            iconSize={16}
          >
            Performance Metrics
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onExportData('error_log')}
            iconName="AlertCircle"
            iconSize={16}
          >
            Error Log
          </Button>
        </div>
      </div>
      {/* Keyboard Shortcuts */}
      <div className="bg-muted/30 border border-border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-foreground mb-3">Keyboard Shortcuts</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Refresh data</span>
            <kbd className="px-2 py-1 bg-muted rounded text-foreground">F5</kbd>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Quick search</span>
            <kbd className="px-2 py-1 bg-muted rounded text-foreground">Ctrl+F</kbd>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Select all</span>
            <kbd className="px-2 py-1 bg-muted rounded text-foreground">Ctrl+A</kbd>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Export data</span>
            <kbd className="px-2 py-1 bg-muted rounded text-foreground">Ctrl+E</kbd>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickActions;